import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getTripFolderAffectedStaffs } from '../../../api/trip-folder/get-trip-folder-affected-staffs'
import { TripFolderEnrollmentSupervisorFormInput } from '../../../lib/types/models/trip-folder/enrollment-supervisor-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type AssignSupervisorToEnrollmentFormProps =
  BaseFormProps<TripFolderEnrollmentSupervisorFormInput> & {
    folderId: number
  }

const formSchema: ZodType<TripFolderEnrollmentSupervisorFormInput> = z.object({
  supervisorId: createNumberSchema('encadrant')
})

const AssignSupervisorToEnrollmentForm = ({
  defaultValues,
  folderId,
  className,
  ...validationButtonProps
}: AssignSupervisorToEnrollmentFormProps) => {
  const form = useForm<TripFolderEnrollmentSupervisorFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { supervisorId } = form.watch()

  const { data: tripFolderAffectedStaffs } = useQuery({
    queryKey: ['trip-folder-affected-staffs', folderId],
    queryFn: () => getTripFolderAffectedStaffs(folderId)
  })

  const selectedStaff = tripFolderAffectedStaffs?.find(
    (affectedStaff) => affectedStaff.staff.id === supervisorId
  )?.staff

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name="supervisorId"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    label={'Encadrant'}
                    dropDownPosition="above"
                    options={tripFolderAffectedStaffs?.map(({ staff }) => ({
                      label: toFullName(staff.firstName, staff.lastName, {
                        options: { mode: 'lastname-firstname' }
                      }),
                      value: staff.id
                    }))}
                    selectedOption={conditionalRender(!!field.value, {
                      label: toFullName(selectedStaff?.firstName, selectedStaff?.lastName, {
                        options: { mode: 'lastname-firstname' }
                      }),
                      value: field.value
                    })}
                    onChange={(value) =>
                      form.setValue('supervisorId', value, { shouldValidate: true })
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default AssignSupervisorToEnrollmentForm
